import React, { useCallback } from "react";
import styled from "styled-components";
import { buyFlowTextStyles, colours, device, mediaQuery } from "@helpers";
import FormError from "./FormError";

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const Label = styled.label`
  ${buyFlowTextStyles.SEMISMALL}
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  font-size: 14px;
  
  ${mediaQuery.tabletAndUp} {
    font-size: 15px;
  }
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid hsl(0, 0%, 80%);
  border-color: ${(props) => {
		if (props.valid) return colours.VALID;
		if (props.isError) return colours.ERROR;
		return "hsl(0, 0%, 80%)";
	}};
  transition: border-color 100ms ease-out;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
`;

export const Input = styled.input`
  ${buyFlowTextStyles.HEADING2}
  caret-color: ${colours.PEACH};
  width: 100%;
  height: 40px;
  padding-left: 1rem;
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: ${colours.MIDNIGHT_BLUE};
  
  ${mediaQuery.desktop} {
    ${buyFlowTextStyles.HEADING1_5}
  }
  
  :focus {
    outline: none;
  }
`;

const CheckBoxText = styled.span`
  max-width: 300px;
  @media (min-width: ${device.mobileM}) {
    max-width: 450px;
  }
  p {
    font-size: 14px;
  }
`;

const FormInput = ({
	autoFocus = false,
	label,
	name,
	required,
	type = "text",
	register = () => {},
	errors,
	clearErrors,
	onChange,
	rightIcon,
	renderCheckbox,
	onKeyDown,
	...props
}) => {
	const getErrorMessage = () => errors?.[name]?.message;

	const clearError = useCallback(() => {
		if (errors?.[name]) {
			clearErrors(name);
		}
	}, [errors, name, clearErrors]);

	const setRef = (ref) => {
		register(ref);
	};
	if (type === "checkbox") {
		return (
			<Container>
				<label htmlFor={name} className={`${required && "required"}`}>
					{label}
				</label>
				<Row checkBoxRowStyling={type === "checkbox"}>
					<Input
						id={name}
						type={type}
						name={name}
						ref={setRef}
						onChange={onChange || clearError}
						register={register}
						errorMessage={getErrorMessage()}
						onKeyDown={onKeyDown}
						{...props}
					/>
					{rightIcon}
					<CheckBoxText>{renderCheckbox}</CheckBoxText>
				</Row>
				{getErrorMessage() && (
					<FormError data-testid={`${name}-error`}>
						{getErrorMessage()}
					</FormError>
				)}
			</Container>
		);
	}

	return (
		<Container>
			{label && (
				<Label htmlFor={name} className={`${required && "required"}`}>
					{label}
				</Label>
			)}
			<InputContainer isError={!!getErrorMessage()}>
				{type === "dob" ? (
					<Input
						id={name}
						type={type}
						name={name}
						ref={setRef}
						onChange={clearError}
						register={register}
						errorMessage={getErrorMessage()}
						{...props}
					/>
				) : (
					<Input
						id={name}
						data-testid={name}
						type={type}
						name={name}
						ref={setRef}
						onChange={onChange || clearError}
						register={register}
						errorMessage={getErrorMessage()}
						onKeyDown={onKeyDown}
						{...props}
					/>
				)}
				{rightIcon}
			</InputContainer>
			{getErrorMessage() && (
				<FormError data-testid={`${name}-error`}>{getErrorMessage()}</FormError>
			)}
		</Container>
	);
};

export default FormInput;
